import { formatDate, parseDate } from "@sembark-travel/datetime-utils"
import { formatMoneyByIntl, TMoney } from "@sembark-travel/money"
import {
	formatRuleConditionLabel,
	formatRuleInstalmentLabels,
	isFallbackRule,
	TCustomerPaymentPreference,
	type TCustomerPaymentPreferencePayload,
	type TPaymentReference,
} from "../CustomerPaymentPreferences"
import type {
	TPaymentPolicy,
	TPaymentPolicyPayload,
	TPaymentRuleSet,
} from "./store"
import { addKeyToFieldArrayItem } from "@sembark-travel/ui-form"
import type { TNewPaymentPolicyFormSchema } from "./NewItem"
import {
	diffDateRangeRules,
	diffListByKey,
	type TDiffRow,
	type TRuleBlockDiffEntry,
} from "../shared/policyDiffUtils"
import type { TDateRange } from "../shared/policies"

export type TPaymentPolicyRuleBlock = {
	valid_from: Date | null
	valid_to: Date | null
	rules: Array<TCustomerPaymentPreference>
}

export function makeEmptyRuleBlock(
	dates?: TDateRange
): TPaymentPolicyRuleBlock {
	return {
		valid_from: dates?.valid_from || null,
		valid_to: dates?.valid_to || null,
		rules: [],
	}
}

export function policyToPayloadRules(
	blocks: Array<TPaymentPolicyRuleBlock>
): TPaymentPolicyPayload["rule_sets"] {
	return (blocks || []).map((block) => ({
		valid_from: block.valid_from
			? formatDate(block.valid_from, "YYYY-MM-DD")
			: null,
		valid_to: block.valid_to ? formatDate(block.valid_to, "YYYY-MM-DD") : null,
		rules: (block.rules || []).map((rule) => Number(rule.id)),
	}))
}

export function formValuesToPayload(
	values: TNewPaymentPolicyFormSchema
): TPaymentPolicyPayload {
	return {
		name: values.name,
		additional_info: values.text || undefined,
		rule_sets: [
			...policyToPayloadRules([values.base_rule]),
			...policyToPayloadRules(values.rules),
		],
	}
}

export function policyVersionToFormValues(
	name: string,
	policy: TPaymentPolicy
): TNewPaymentPolicyFormSchema {
	const ruleSets = policy.rule_sets || []
	const baseRule = ruleSets.find(
		(r) => r.valid_from == null && r.valid_to == null
	)
	const dateRules = ruleSets.filter(
		(r) => r.valid_from != null || r.valid_to != null
	)

	function ruleToFormValue(rule: TPaymentRuleSet): TPaymentPolicyRuleBlock {
		return addKeyToFieldArrayItem({
			valid_from: rule.valid_from ? parseDate(rule.valid_from) : null,
			valid_to: rule.valid_to ? parseDate(rule.valid_to) : null,
			rules: rule.rules || [],
		}) as TPaymentPolicyRuleBlock
	}

	return {
		name,
		text: policy.additional_info || "",
		base_rule: baseRule ? ruleToFormValue(baseRule) : makeEmptyRuleBlock(),
		rules: dateRules.map(ruleToFormValue),
	}
}

// Every booking has to resolve to some rule, so the base block needs a rule
// without any condition.
export function hasFallbackRule(
	rules?: Array<TCustomerPaymentPreference>
): boolean {
	return (rules || []).some(isFallbackRule)
}

// The fallback rule applies only when no other rule of the block matches, so
// it is always kept at the end. An already ordered list is returned as is, so
// `PinFallbackRuleToEnd` doesn't change the field again.
export function keepFallbackRuleLast(
	rules: Array<TCustomerPaymentPreference>
): Array<TCustomerPaymentPreference> {
	const others = rules.filter((rule) => !isFallbackRule(rule))
	const fallbacks = rules.filter(isFallbackRule)
	const sorted = others.concat(fallbacks)
	return sorted.every((rule, index) => rule === rules[index]) ? rules : sorted
}

// The rules of a block are applied in the order they are listed in, so moving
// a rule changes when it is applied.
export function moveRule<T>(
	rules: Array<T>,
	from: number,
	to: number
): Array<T> {
	if (
		from === to ||
		from < 0 ||
		to < 0 ||
		from >= rules.length ||
		to >= rules.length
	) {
		return rules
	}
	const next = rules.slice()
	const [moved] = next.splice(from, 1)
	next.splice(to, 0, moved)
	return next
}

// Presets offered during onboarding. Each one is a Fallback Rule whose
// instalments are due on the day of its reference event.
export const PAYMENT_POLICY_PRESETS = [
	{
		id: "half_booking_half_checkin",
		label: "50% on Booking, 50% on Check-in",
		breakdowns: [
			{ reference: "Booking", percent: 50 },
			{ reference: "Check-in", percent: 50 },
		],
	},
	{
		id: "full_booking",
		label: "100% on Booking",
		breakdowns: [{ reference: "Booking", percent: 100 }],
	},
	{
		id: "full_checkin",
		label: "100% on Check-in",
		breakdowns: [{ reference: "Check-in", percent: 100 }],
	},
] as const

export type TPaymentPolicyPreset = (typeof PAYMENT_POLICY_PRESETS)[number]
export type TPaymentPolicyPresetId = TPaymentPolicyPreset["id"]

function normalizeReferenceName(name: string) {
	return name.trim().toLowerCase()
}

// References are tenant data, so the preset's reference events are looked up
// by their names.
export function getPresetReferenceIds(
	preset: TPaymentPolicyPreset,
	references: Array<TPaymentReference>
):
	| { referenceIds: Record<string, number>; missing: [] }
	| { referenceIds: null; missing: Array<string> } {
	const referenceIds: Record<string, number> = {}
	const missing: Array<string> = []
	for (const { reference } of preset.breakdowns) {
		const found = references.find(
			(r) =>
				normalizeReferenceName(r.name) === normalizeReferenceName(reference)
		)
		if (found) {
			referenceIds[reference] = Number(found.id)
		} else if (!missing.includes(reference)) {
			missing.push(reference)
		}
	}
	return missing.length
		? { referenceIds: null, missing }
		: { referenceIds, missing: [] }
}

export function presetToRulePayload(
	preset: TPaymentPolicyPreset,
	referenceIds: Record<string, number>
): TCustomerPaymentPreferencePayload {
	return {
		is_fallback_rule: 1,
		breakdowns: preset.breakdowns.map(({ reference, percent }) => ({
			reference: referenceIds[reference],
			day_offset: 0,
			instalment_breakup_in_percent: percent,
		})),
		is_include_flight_cost_in_first_instalment: 0,
	}
}

// Compared as numbers, the same way a selected rule is removed.
export function isSameRule(
	a: TCustomerPaymentPreference,
	b: TCustomerPaymentPreference
): boolean {
	return Number(a.id) === Number(b.id)
}

// The create API returns the existing rule when one with the same details
// exists, so it replaces its copy instead of being listed twice.
export function putRuleFirst(
	rules: Array<TCustomerPaymentPreference>,
	rule: TCustomerPaymentPreference
): Array<TCustomerPaymentPreference> {
	return [rule].concat(rules.filter((r) => !isSameRule(r, rule)))
}

export type TPaymentRuleSetDiffEntry = TRuleBlockDiffEntry<
	TPaymentRuleSet,
	TCustomerPaymentPreference
>

// Identity key for a rule item used during diffing. `id` is excluded because
// every edit mints new ids — a rule that did not change would otherwise always
// read as removed + added.
export function paymentRuleItemKey(item: TCustomerPaymentPreference): string {
	const condition = item.preference_condition?.trim()
	const breakdowns = item.breakdowns
		.map(
			(b) => `${b.reference}-${b.day_offset}-${b.instalment_breakup_in_percent}`
		)
		.sort()
		.join("|")
	const minAmounts = item.min_first_instalment_amounts
		.map((a) => `${a.amount.currency}-${a.amount.amount}`)
		.sort()
		.join("|")
	return `${condition}::${breakdowns}::${minAmounts}`
}

// Compact multi-line label for a rule item used in the diff cells.
export function formatPaymentRuleItemLabel(
	item: TCustomerPaymentPreference
): string {
	const condition = formatRuleConditionLabel(item)

	// Min-amount line, e.g. "Minimum Amount: INR 50,000"
	const minAmountLines = item.min_first_instalment_amounts
		.filter((a) => a.amount.amount)
		.map((a) => `Minimum Amount: ${formatMinAmount(a.amount)}`)

	const instalmentLines = formatRuleInstalmentLabels(item)

	return [condition, ...minAmountLines, instalmentLines.join(" • ")]
		.filter(Boolean)
		.join("\n")
}

// Structured version used by the diff cell so it can apply different font
// weights: condition line is semibold, body lines (min-amount + instalments)
// are normal weight.
export function formatPaymentRuleItemParts(item: TCustomerPaymentPreference): {
	condition: string
	body: string
} {
	const condition = formatRuleConditionLabel(item)

	const minAmountLines = item.min_first_instalment_amounts
		.filter((a) => a.amount.amount)
		.map((a) => `Minimum Amount: ${formatMinAmount(a.amount)}`)

	const instalmentLines = formatRuleInstalmentLabels(item)

	const body = [...minAmountLines, instalmentLines.join(" • ")]
		.filter(Boolean)
		.join("\n")

	return { condition, body }
}

// Money amounts arrive in the smallest unit of the currency, so they have to go
// through the money lib rather than be printed directly — the same treatment
// `RuleSelectDialog` gives this field.
function formatMinAmount(amount: TMoney): string {
	return formatMoneyByIntl(amount, { showCurrency: true })
}

// The rules of a block are applied in order, so unlike cancellation conditions
// they are diffed in the order they are listed in.
export function diffPaymentRuleItems(
	previousItems: ReadonlyArray<TCustomerPaymentPreference>,
	currentItems: ReadonlyArray<TCustomerPaymentPreference>
): Array<TDiffRow<TCustomerPaymentPreference>> {
	return diffListByKey(previousItems, currentItems, paymentRuleItemKey)
}

export function diffPaymentRuleSets(
	previousRuleSets: ReadonlyArray<TPaymentRuleSet>,
	currentRuleSets: ReadonlyArray<TPaymentRuleSet>
): Array<TPaymentRuleSetDiffEntry> {
	return diffDateRangeRules(
		previousRuleSets,
		currentRuleSets,
		(previous, current) =>
			diffPaymentRuleItems(previous?.rules || [], current?.rules || [])
	)
}

export {
	diffMarkdownText,
	type TPolicyTextDiffEntry,
} from "../shared/policyDiffUtils"
